import { ButtonTheme, Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { arrayHasSameMembers } from 'lib/arrayHasSameMembers/arrayHasSameMembers';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { arrayUnique, filterArrayEmpty, randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import {
  cloneDeep,
  intersection,
  isEqual,
  sortBy,
  toInteger,
  union,
  without,
} from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  REMOTE_ASESETUP_CONTROLDELETE,
  REMOTE_ASESETUP_QUESTIONDELETE,
  REMOTE_ASESETUP_ANSWEROPEN,
  REMOTE_ASESETUP_CHECKLISTITEMOPEN,
  REMOTE_ASESETUP_CONTROLOPEN,
  REMOTE_ASESETUP_QUESTIONCLOSED,
  REMOTE_ASESETUP_QUESTIONSAVE,
  SET_INCOMING_VALUE,
  doAseSetupLibraryQuestionLinksChosen,
  eventReorderScreenDone,
} from 'store/actions';
import { selectIncomingValue } from 'store/app/selectors';
import { selectUserIsSupport } from 'store/auth/selectors';
import {
  selectAseSetupQuestionHasControls,
  selectAseSetupLibraryControlIdsAlphaOrdered,
  selectAseSetupLibraryQuestion,
  selectAseSetupLibraryQuestionHasAnswers,
  selectAseSetupLibraryQuestionHasChecklistItems,
  selectAseSetupQuestionId,
  selectAseSetupLibrarySection,
  selectAseSetupLibraryQuestionAnswers,
  selectAseSetupLibraryQuestions,
  selectAseSetupLibraryChecklistItems,
  selectAseSetupLibraryAnswers,
} from 'store/remote/selectors';
import { AssessmentLibrary } from 'store/remote/types';
import { AssessmentQuestionType } from 'types/types';
import { FlatList, ListRenderItemInfo } from 'react-native';
import { AnswerRow } from './Rows/AnswerRow';
import { ChecklistItemRow } from './Rows/ChecklistItemRow';
import { FindingRow } from './Rows/FindingRow';

export const QuestionScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYQUESTION>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const userIsSupport = useSelector(selectUserIsSupport);

  const linkControlsSelectionKey = 'AseSetupAnswerLinkControls';
  const addControlSelectionKey = 'AseSetupAnswerAddControl';

  const {
    sectionId: inSectionId,
    parentQuestionId: inParentQuestionId,
    type: inType,
  } = route.params;

  const linkControlIds = useSelector((state) =>
    selectIncomingValue(state, linkControlsSelectionKey),
  ) as unknown as number[] | undefined;

  const addControlId = useSelector((state) =>
    selectIncomingValue(state, addControlSelectionKey),
  ) as unknown as number | undefined;

  const questionId = useSelector(selectAseSetupQuestionId);
  const initIdRef = React.useRef(questionId);

  const question: AssessmentLibrary['questions'][number] = useSelector(
    (state) => selectAseSetupLibraryQuestion(state, questionId || -1),
  ) || {
    id: 0,
    answerId: null,
    sectionId: inSectionId,
    question: '',
    description: '',
    reportDescription: '',
    ordering: 0,
    type: inType || AssessmentQuestionType.checklist,
    templateIds: [],
    linkedQuestionIds: [],
  };

  const questions = useSelector(selectAseSetupLibraryQuestions);

  const parentQuestion = questions[inParentQuestionId || -1];

  const parentAnswers = useSelector((state) =>
    selectAseSetupLibraryQuestionAnswers(state, inParentQuestionId || -1),
  );

  const section = useSelector((state) =>
    selectAseSetupLibrarySection(state, question.sectionId || -1),
  );
  const checklistItemIds = useSelector((state) =>
    selectAseSetupLibraryQuestionHasChecklistItems(state, question.id),
  );
  const checklistItems = useSelector(selectAseSetupLibraryChecklistItems);
  const answers = useSelector(selectAseSetupLibraryAnswers);

  const answerIds = useSelector((state) =>
    selectAseSetupLibraryQuestionHasAnswers(state, question.id),
  );

  // We need all action IDs so we can order them correctly
  const libraryControlIds = useSelector((state) =>
    selectAseSetupLibraryControlIdsAlphaOrdered(state, '_'),
  );

  const controlIds = useSelector((state) =>
    selectAseSetupQuestionHasControls(state, question.id),
  );

  const afterCreate = React.useRef<
    | undefined
    | 'addchecklistitem'
    | 'addanswer'
    | 'addcontrol'
    | 'linkcontrols'
    | 'linkquestions'
  >(undefined);

  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(question));
  const dataChecklistItemIds = React.useRef(cloneDeep(checklistItemIds));
  const dataAnswerIds = React.useRef(cloneDeep(answerIds));
  const dataControlIds = React.useRef<number[]>(controlIds);

  // Ensure there aren't deleted controls in the list
  dataControlIds.current = intersection(
    dataControlIds.current,
    libraryControlIds,
  );

  const checkIsDirty = () =>
    !isEqual(question, data.current) ||
    !isEqual(controlIds, dataControlIds.current) ||
    !isEqual(checklistItemIds, dataChecklistItemIds.current) ||
    !isEqual(answerIds, dataAnswerIds.current);

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== question.id) {
    data.current = cloneDeep(question);
  }
  isDirty.current = checkIsDirty();

  const shouldReload = () => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  };

  const handleDeleteControl = (controlId: number) =>
    dispatch(REMOTE_ASESETUP_CONTROLDELETE({ id: controlId }));

  const handleUnlinkControl = (controlId: number) => {
    dataControlIds.current = without(dataControlIds.current, controlId);
    forceUpdate();
  };

  const handleChangeWithName = <T extends keyof typeof data.current>(
    name: T,
    value: (typeof data.current)[T],
  ) => {
    data.current = {
      ...data.current,
      [name]: value,
    };
    shouldReload();
  };

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = (close: boolean = false) => {
    const errors: string[] = [];
    if (!question.id && !data.current.sectionId && !data.current.answerId) {
      errors.push('Please choose a parent answer');
    }
    if (!question.id && data.current.question.trim() === '') {
      errors.push(
        data.current.type === AssessmentQuestionType.checklist
          ? 'Please enter a title'
          : 'Please enter a question',
      );
    }
    if (errors.length) {
      alert({
        text: errors.join('\n\n'),
      });
      return;
    }
    dispatch(
      REMOTE_ASESETUP_QUESTIONSAVE({
        id: questionId,
        data: data.current,
        controlIds: dataControlIds.current,
        orderedChecklistItemIds: dataChecklistItemIds.current,
        orderedAnswerIds: dataAnswerIds.current,
        linkedQuestionIds: data.current.linkedQuestionIds,
        addData: {
          sectionId: question.sectionId,
          answerId: data.current.answerId,
          type: question.type,
        },
        close,
      }),
    );
  };

  const handleSavePress = () => {
    if (isDirty.current || !question.id) {
      handleSave(true);
    } else {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  const handleSetParentAnswer = (answerId: number) => {
    data.current.answerId = answerId;
    forceUpdate();
  };

  const handleOpenControl = React.useCallback(
    (id?: number) =>
      dispatch(
        REMOTE_ASESETUP_CONTROLOPEN({
          id,
          incomingValueNewIdKey: addControlSelectionKey,
          allowLinkManagement: false,
        }),
      ),
    [dispatch],
  );

  const doAfterCreate = React.useCallback(() => {
    switch (afterCreate.current) {
      case 'addchecklistitem':
        if (questionId) {
          dispatch(
            REMOTE_ASESETUP_CHECKLISTITEMOPEN({
              questionId,
            }),
          );
        }
        break;
      case 'addanswer':
        if (questionId) {
          dispatch(
            REMOTE_ASESETUP_ANSWEROPEN({
              questionId,
            }),
          );
        }
        break;
      case 'addcontrol':
        handleOpenControl();
        break;
      case 'linkcontrols':
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.ASESETUPLIBRARYCONTROLSLIST,
            params: {
              incomingValueKey: linkControlsSelectionKey,
              hideIds: dataControlIds.current,
            },
          }),
        );
        break;
      case 'linkquestions':
        if (questionId) {
          dispatch(
            NAVIGATE_MODAL({
              screen: ModalScreens.ASESETUPLIBRARYLINKQUESTIONS,
              params: {
                hideIds: [questionId, ...data.current.linkedQuestionIds],
              },
            }),
          );
        }
        break;
      default:
    }
  }, [dispatch, handleOpenControl, questionId]);

  const handleAddChecklistItem = () => {
    afterCreate.current = 'addchecklistitem';
    if (!question.id) {
      handleSave();
      return;
    }
    doAfterCreate();
  };

  const handleAddAnswer = () => {
    afterCreate.current = 'addanswer';
    if (!question.id) {
      handleSave();
      return;
    }
    doAfterCreate();
  };

  const handleLinkQuestions = () => {
    afterCreate.current = 'linkquestions';
    if (!question.id) {
      handleSave();
      return;
    }
    doAfterCreate();
  };

  const handleAddControl = (link: boolean = false) => {
    afterCreate.current = link ? 'linkcontrols' : 'addcontrol';
    if (!question.id) {
      handleSave();
      return;
    }
    doAfterCreate();
  };

  const handleOpenChecklistItem = React.useCallback(
    (checklistItemId: number) => {
      dispatch(
        REMOTE_ASESETUP_CHECKLISTITEMOPEN({
          id: checklistItemId,
        }),
      );
    },
    [dispatch],
  );

  const handleOpenAnswer = React.useCallback(
    (answerId: number) => {
      dispatch(
        REMOTE_ASESETUP_ANSWEROPEN({
          id: answerId,
        }),
      );
    },
    [dispatch],
  );

  const handleDelete = () =>
    dispatch(
      REMOTE_ASESETUP_QUESTIONDELETE({
        id: question.id,
      }),
    );

  const renderChecklistItemRow = React.useCallback(
    ({ item }: ListRenderItemInfo<number>) => {
      return (
        <ChecklistItemRow
          id={item}
          key={item}
          onPress={() => handleOpenChecklistItem(item)}
        />
      );
    },
    [handleOpenChecklistItem],
  );

  const renderAnswerRow = React.useCallback(
    ({ item }: ListRenderItemInfo<number>) => {
      return (
        <AnswerRow
          id={item}
          key={item}
          onPress={() => handleOpenAnswer(item)}
        />
      );
    },
    [handleOpenAnswer],
  );

  const handleUnlinkQuestion = (unlinkQuestionId: number) => {
    data.current = {
      ...data.current,
      linkedQuestionIds: without(
        data.current.linkedQuestionIds,
        unlinkQuestionId,
      ),
    };
    forceUpdate();
  };

  React.useEffect(() => {
    if (linkControlIds?.length) {
      dataControlIds.current = union(dataControlIds.current, linkControlIds);
      dispatch(SET_INCOMING_VALUE({ key: linkControlsSelectionKey }));
    }
  }, [dispatch, linkControlIds]);

  React.useEffect(() => {
    if (addControlId) {
      dataControlIds.current = union(dataControlIds.current, [addControlId]);
      dispatch(SET_INCOMING_VALUE({ key: addControlSelectionKey }));
    }
  }, [dispatch, addControlId]);

  React.useEffect(() => {
    if (!arrayHasSameMembers(dataChecklistItemIds.current, checklistItemIds)) {
      dataChecklistItemIds.current = union(
        intersection(dataChecklistItemIds.current, checklistItemIds),
        checklistItemIds,
      );
      forceUpdate();
    }
  }, [checklistItemIds, forceUpdate]);

  React.useEffect(() => {
    if (!arrayHasSameMembers(dataAnswerIds.current, answerIds)) {
      dataAnswerIds.current = union(
        intersection(dataAnswerIds.current, answerIds),
        answerIds,
      );
      forceUpdate();
    }
  }, [answerIds, forceUpdate]);

  React.useEffect(() => {
    if (initIdRef.current && !questionId) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (initIdRef.current !== questionId) {
      initIdRef.current = questionId;
      doAfterCreate();
    }
  }, [dispatch, doAfterCreate, questionId]);

  React.useEffect(() => {
    return () => {
      dispatch(REMOTE_ASESETUP_QUESTIONCLOSED());
    };
  }, [dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  const reorderAnswersEventKey = React.useRef(randomKey());
  const reorderChecklistItemsEventKey = React.useRef(randomKey());

  React.useEffect(() => {
    const events = [
      doAddEvent(eventReorderScreenDone, (action) => {
        if (action.payload.key === reorderAnswersEventKey.current) {
          dataAnswerIds.current = action.payload.stringValues.map(toInteger);
          forceUpdate();
        } else if (
          action.payload.key === reorderChecklistItemsEventKey.current
        ) {
          dataChecklistItemIds.current =
            action.payload.stringValues.map(toInteger);
          forceUpdate();
        }
      }),
      doAddEvent(doAseSetupLibraryQuestionLinksChosen, (action) => {
        data.current = {
          ...data.current,
          linkedQuestionIds: arrayUnique([
            ...data.current.linkedQuestionIds,
            ...action.payload,
          ]),
        };
        forceUpdate();
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate]);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={
            data.current.type === AssessmentQuestionType.checklist
              ? questionId
                ? 'Checklist'
                : `New ${parentQuestion ? 'Sub-' : ''}Checklist`
              : questionId
                ? 'Question'
                : `New ${parentQuestion ? 'Sub-' : ''}Question`
          }
          subheading={section ? `Section: ${section.name}` : ''}
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={
            questionId || isDirty.current ? handleSavePress : null
          }
        />
      }
    >
      {parentQuestion && (
        <Card
          label="Parent Answer"
          explanation={`${
            data.current.type === AssessmentQuestionType.checklist
              ? 'Checklist'
              : 'Question'
          } will appear when this answer is chosen`}
        >
          <CardRow
            label={parentQuestion.question}
            labelNumberOfLines={0}
            icon={ICONS.CircleQSolid}
            iconColor={theme.colors.question}
          />
          {parentAnswers.map((parentAnswer) => (
            <CardRow
              key={parentAnswer.id}
              label={parentAnswer.answer}
              labelColor={parentAnswer.negative ? theme.colors.red : undefined}
              labelNumberOfLines={0}
              radioValue={parentAnswer.id === data.current.answerId}
              onPress={() => handleSetParentAnswer(parentAnswer.id)}
            />
          ))}
        </Card>
      )}
      <Card explanation="This description is shown to assessors but doesn't appear on reports">
        <TextAreaField
          name="question"
          onChangeWithName={handleChangeWithName}
          placeholder={
            data.current.type === AssessmentQuestionType.checklist
              ? 'Title'
              : 'Question'
          }
          value={data.current.question}
          onEsc={handleClose}
          autoFocusWeb={!parentQuestion}
          autoFocusNative={!parentQuestion && !data.current.id}
          autoFocusDelay
        />
        <TextAreaField
          name="description"
          onChangeWithName={handleChangeWithName}
          placeholder="Description"
          value={data.current.description}
          onEsc={handleClose}
        />
      </Card>

      <Card explanation="This report description is not shown to assessors but does appear on reports">
        <TextAreaField
          name="reportDescription"
          onChangeWithName={handleChangeWithName}
          placeholder="Report Description"
          value={data.current.reportDescription}
          onEsc={handleClose}
        />
      </Card>

      {data.current.type === AssessmentQuestionType.checklist && (
        <Card
          label="Checklist Items"
          labelLinkText="New"
          onLabelLinkPress={handleAddChecklistItem}
          labelLinkText2="Reorder"
          onLabelLinkPress2={
            dataChecklistItemIds.current.length > 1
              ? () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.REORDER,
                      params: {
                        title: 'Reorder Checklist Items',
                        options: dataChecklistItemIds.current.reduce<
                          { value: string; name: string }[]
                        >((acc, checklistItemId) => {
                          const checklistItem = checklistItems[checklistItemId];
                          if (checklistItem) {
                            acc.push({
                              value: `${checklistItem.id}`,
                              name: checklistItem.text,
                            });
                          }
                          return acc;
                        }, []),
                        eventKey: reorderChecklistItemsEventKey.current,
                      },
                    }),
                  )
              : undefined
          }
        >
          {dataChecklistItemIds.current.length ? (
            <FlatList
              data={dataChecklistItemIds.current}
              renderItem={renderChecklistItemRow}
              keyExtractor={(item) => `draggable-group-${item}`}
              ItemSeparatorComponent={() => <SeparatorHorizontal />}
            />
          ) : (
            <CardRow value="No checklist items added yet" />
          )}
        </Card>
      )}

      {data.current.type === AssessmentQuestionType.question && (
        <Card
          label="Answers"
          labelLinkText="New"
          onLabelLinkPress={handleAddAnswer}
          labelLinkText2="Reorder"
          onLabelLinkPress2={
            dataAnswerIds.current.length > 1
              ? () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.REORDER,
                      params: {
                        title: 'Reorder Answers',
                        options: dataAnswerIds.current.reduce<
                          { value: string; name: string }[]
                        >((acc, answerId) => {
                          const answer = answers[answerId];
                          if (answer) {
                            acc.push({
                              value: `${answer.id}`,
                              name: answer.answer,
                            });
                          }
                          return acc;
                        }, []),
                        eventKey: reorderAnswersEventKey.current,
                      },
                    }),
                  )
              : undefined
          }
        >
          {dataAnswerIds.current.length ? (
            <FlatList
              data={dataAnswerIds.current}
              renderItem={renderAnswerRow}
              keyExtractor={(item) => `draggable-group-${item}`}
              ItemSeparatorComponent={() => <SeparatorHorizontal />}
            />
          ) : (
            <CardRow value="No answers added yet" />
          )}
        </Card>
      )}

      {data.current.type === AssessmentQuestionType.question && (
        <Card
          label="Controls"
          labelLinkText="New"
          onLabelLinkPress={() => handleAddControl(false)}
          labelLinkText2="Link"
          onLabelLinkPress2={() => handleAddControl(true)}
        >
          {dataControlIds.current.length ? (
            libraryControlIds
              .filter((controlId) => dataControlIds.current.includes(controlId))
              .map((controlId) => (
                <FindingRow
                  id={controlId}
                  key={controlId}
                  type="control"
                  forChecklistId={0}
                  forAnswerId={0}
                  forQuestionId={questionId || -1}
                  onPress={() => handleOpenControl(controlId)}
                  inPositive
                  inNegative
                  onDelete={() => handleDeleteControl(controlId)}
                  onUnlink={() => handleUnlinkControl(controlId)}
                />
              ))
          ) : (
            <CardRow value="No controls added yet" />
          )}
        </Card>
      )}

      {userIsSupport && (
        <Card
          label="Linked Questions"
          labelLinkText="Link"
          onLabelLinkPress={handleLinkQuestions}
        >
          {data.current.linkedQuestionIds.length ? (
            sortBy(
              data.current.linkedQuestionIds
                .map((linkedQuestionId) => questions[linkedQuestionId])
                .filter(filterArrayEmpty),
              'question',
            ).map((q) => (
              <CardRow
                key={q.id}
                label={q.question}
                labelNumberOfLines={0}
                showMenuOnPress
                menuOptions={[
                  {
                    text: 'Unlink Question',
                    icon: ICONS.LinkHorizontalSlashLight,
                    onPress: () => handleUnlinkQuestion(q.id),
                  },
                ]}
              />
            ))
          ) : (
            <CardRow value="No linked questions" rbs />
          )}
        </Card>
      )}

      {!!question.id && (
        <CardGeneric>
          <Button
            text={
              question.type === AssessmentQuestionType.checklist
                ? 'Delete Checklist'
                : 'Delete Question'
            }
            onPress={handleDelete}
            buttonTheme={ButtonTheme.themeless}
            destructive
            style={{ alignSelf: 'center' }}
          />
        </CardGeneric>
      )}
    </PageScrollView>
  );
};
